/**
 * Journey chapters, diary days and Today next-action selection.
 * Never infers physical location from a scheduled time.
 * Transfer clock times come from startLocal/plannedDeparture so they sort on the day.
 * Arrival check-in uses the hotel from-time so it is not dumped after evening plans.
 */

import type { Idea, Place, Stay, TripDocument, TripEvent } from "./schema.ts";
import { tripLedger } from "./ledger.ts";
import { formatMinor } from "./money.ts";
import { chronologicalSchedule, upcomingPayments } from "./upcoming.ts";
import {
  calendarDaysInclusive,
  clockHm,
  eachDateInclusive,
  formatDateLabel,
  formatDurationPretty,
  instantMs,
  localTimeOnDateMs,
  nightsBetween,
  todayInZone,
  tripPhase,
} from "./time.ts";

export type Chapter = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  nights: number;
  place?: Place;
  stay?: Stay;
  summary: string;
  imageAssetId: string;
};

export type RouteStop = {
  id: string;
  label: string;
  kind: "stay" | "travel";
  href: string;
  startDate: string;
  endDate: string;
};

export type DiaryItem = {
  id: string;
  kind: TripEvent["kind"] | "accommodation";
  title: string;
  timeLabel: string | null;
  flexible: boolean;
  confidence?: string | null;
  note?: string | null;
  href: string;
  event?: TripEvent;
};

/** Night 1 is the arrival date. Checkout day is outside the stay. */
export function stayNightIndex(stay: Stay, date: string): { night: number; nights: number } | null {
  if (date < stay.startDate || date >= stay.endDate) return null;
  return { night: nightsBetween(stay.startDate, date) + 1, nights: stay.nights };
}

export function stayNightLabel(stay: Stay, date: string, hotelName?: string | null): string | null {
  const progress = stayNightIndex(stay, date);
  if (!progress) return null;
  const base = `Day ${progress.night} of ${progress.nights}`;
  return hotelName ? `${base} at ${hotelName}` : base;
}

export function chaptersFromDoc(doc: TripDocument): Chapter[] {
  const summaries: Record<string, string> = {
    "Chiang Mai": "Old-city base at Narinthra Heritage. Extra night and three-night bookings sit in one continuous stay.",
    "Hang Dong": "Two Chantra Khiri rooms for the same four nights — villa and suite remain separate reservations.",
    "Koh Samet": "Island days after the BKK van to the mainland pier. Boat crossing is not in the van package.",
    Bangkok: "One night in Silom before the evening flights home.",
  };
  const images: Record<string, string> = {
    "Chiang Mai": "chiang-mai",
    "Hang Dong": "hang-dong",
    "Koh Samet": "koh-samet",
    Bangkok: "bangkok",
  };
  return doc.trip.chapterOrder.map((name) => {
    const stay = doc.stays.find((s) => s.chapter === name);
    const place = stay ? doc.places.find((p) => p.id === stay.placeId) : undefined;
    return {
      id: stay?.id ?? name.toLowerCase().replaceAll(" ", "-"),
      name,
      startDate: stay?.startDate ?? doc.trip.startDate,
      endDate: stay?.endDate ?? doc.trip.endDate,
      nights: stay?.nights ?? nightsBetween(doc.trip.startDate, doc.trip.endDate),
      place,
      stay,
      summary: summaries[name] ?? place?.name ?? name,
      imageAssetId: images[name] ?? "journey",
    };
  });
}

export function tripNightCount(doc: TripDocument): number {
  return doc.stays.reduce((sum, stay) => sum + stay.nights, 0);
}

export function tripCalendarDays(doc: TripDocument): number {
  return calendarDaysInclusive(doc.trip.startDate, doc.trip.endDate);
}

export function stayForDate(doc: TripDocument, date: string): Stay | undefined {
  return doc.stays.find((stay) => stay.startDate <= date && date < stay.endDate);
}

export function journeyRoute(doc: TripDocument): RouteStop[] {
  const chapters = chaptersFromDoc(doc);
  const stops: RouteStop[] = [];
  const first = chapters[0];
  if (first && doc.trip.startDate < first.startDate) {
    stops.push({
      id: "travel-outbound",
      label: "Outbound",
      kind: "travel",
      href: `/journey/day/${doc.trip.startDate}`,
      startDate: doc.trip.startDate,
      endDate: first.startDate,
    });
  }
  chapters.forEach((chapter, index) => {
    stops.push({
      id: chapter.id,
      label: chapter.name,
      kind: "stay",
      href: `/journey/stay/${chapter.id}`,
      startDate: chapter.startDate,
      endDate: chapter.endDate,
    });
    const next = chapters[index + 1];
    if (next && chapter.endDate < next.startDate) {
      stops.push({
        id: `travel-${chapter.id}`,
        label: "Travel day",
        kind: "travel",
        href: `/journey/day/${chapter.endDate}`,
        startDate: chapter.endDate,
        endDate: next.startDate,
      });
    }
  });
  const last = chapters[chapters.length - 1];
  if (last && last.endDate < doc.trip.endDate) {
    stops.push({
      id: "travel-home",
      label: "Homeward",
      kind: "travel",
      href: `/journey/day/${last.endDate}`,
      startDate: last.endDate,
      endDate: doc.trip.endDate,
    });
  }
  return stops;
}

export function ideasForDate(doc: TripDocument, date: string): Idea[] {
  return doc.ideas.filter((idea) => idea.date === date && idea.status !== "booked" && idea.status !== "archived");
}

export function placesForDate(doc: TripDocument, date: string): Place[] {
  const ids = new Set<string>();
  const stay = stayForDate(doc, date);
  if (stay) ids.add(stay.placeId);
  for (const event of doc.events) {
    if (event.archived || event.date !== date) continue;
    if (event.meetingPlaceId) ids.add(event.meetingPlaceId);
    if (event.toPlaceId) ids.add(event.toPlaceId);
    if (event.fromPlaceId) ids.add(event.fromPlaceId);
  }
  return doc.places.filter((place) => ids.has(place.id));
}

export function placesForChapter(doc: TripDocument, chapterName: string): Place[] {
  const stay = doc.stays.find((s) => s.chapter === chapterName);
  if (!stay) return [];
  const dates = eachDateInclusive(stay.startDate, stay.endDate);
  const found = new Map<string, Place>();
  for (const date of dates) {
    for (const place of placesForDate(doc, date)) found.set(place.id, place);
  }
  const home = stay ? doc.places.find((p) => p.id === stay.placeId) : undefined;
  if (home) found.set(home.id, home);
  return [...found.values()];
}

function flightById(doc: TripDocument, id?: string | null) {
  if (!id) return undefined;
  return doc.flights.find((flight) => flight.id === id);
}

export function diaryEventTimeLabel(event: TripEvent): string | null {
  const clock = clockHm(event.startLocal ?? event.plannedDeparture);
  if (clock) return clock;
  if (event.pickupMode === "when_ready") return "When you're ready";
  if (event.pickupMode === "after_arrival") return "After landing";
  return null;
}

function eventClockMs(event: TripEvent, doc: TripDocument): number | null {
  const time = event.startLocal ?? event.plannedDeparture;
  if (time) {
    const fromTime = localTimeOnDateMs(event.date, time, event.timezone);
    if (fromTime != null) return fromTime;
  }
  if (event.pickupMode === "when_ready" || event.pickupMode === "after_arrival") {
    const linked = flightById(doc, event.linkedFlightId ?? event.flightId);
    if (linked) return instantMs(linked.arrival.instant) + 60_000;
  }
  return null;
}

function eventEndMs(event: TripEvent, startMs: number): number {
  const time = event.endLocal ?? event.plannedArrival;
  if (time) {
    const parsed = localTimeOnDateMs(event.date, time, event.timezone);
    if (parsed != null && parsed > startMs) return parsed;
  }
  if (event.pickupMode === "when_ready") return startMs + 6 * 60 * 60 * 1000;
  return startMs + 2 * 60 * 60 * 1000;
}

export function inFlightNow(doc: TripDocument, now: Date): DiaryItem | null {
  const nowMs = now.getTime();
  for (const flight of doc.flights) {
    if (flight.progressStatus === "complete") continue;
    const start = instantMs(flight.departure.instant);
    const end = instantMs(flight.arrival.instant);
    if (start <= nowMs && nowMs < end) {
      return {
        id: flight.id,
        kind: "flight",
        title: `${flight.number} ${flight.departure.airport}–${flight.arrival.airport}`,
        timeLabel: "Scheduled in flight",
        flexible: false,
        href: `/journey/flight/${flight.id}`,
      };
    }
  }
  return null;
}

function diaryFlightItem(flight: TripDocument["flights"][number], date: string): DiaryItem {
  const depDate = flight.departure.local.slice(0, 10);
  const arrDate = flight.arrival.local.slice(0, 10);
  const continuing = depDate !== arrDate && arrDate === date;
  return {
    id: flight.id,
    kind: "flight",
    title: continuing
      ? `${flight.number} arrives ${flight.arrival.airport}`
      : `${flight.number} ${flight.departure.airport}–${flight.arrival.airport}`,
    timeLabel: continuing
      ? `${flight.arrival.local.slice(11, 16)} ${flight.arrival.airport}`
      : `${flight.departure.local.slice(11, 16)} ${flight.departure.airport}`,
    flexible: false,
    confidence: flight.evidenceStatus,
    href: `/journey/flight/${flight.id}`,
  };
}

export function itemsForDate(doc: TripDocument, date: string): DiaryItem[] {
  const ranked: Array<DiaryItem & { sortMs: number }> = [];
  for (const flight of doc.flights) {
    const depDate = flight.departure.local.slice(0, 10);
    const arrDate = flight.arrival.local.slice(0, 10);
    if (depDate === date || arrDate === date) {
      const continuing = depDate !== arrDate && arrDate === date;
      ranked.push({
        ...diaryFlightItem(flight, date),
        sortMs: instantMs(continuing ? flight.arrival.instant : flight.departure.instant),
      });
    }
  }

  for (const event of doc.events) {
    if (event.kind === "flight" || event.archived) continue;
    if (event.date !== date) continue;
    const time = event.startLocal ?? event.plannedDeparture;
    const startMs = eventClockMs(event, doc);
    const flexible = event.pickupMode === "when_ready"
      || event.pickupMode === "after_arrival"
      || !time
      || Boolean(event.startConfidence?.includes("provisional"));
    ranked.push({
      id: event.id,
      kind: event.kind,
      title: eventTitle(event, doc),
      timeLabel: diaryEventTimeLabel(event),
      flexible,
      confidence: event.startConfidence ?? event.timeConfidence ?? event.dateConfidence,
      href: eventHref(event),
      event,
      sortMs: startMs ?? (1e15 + (event.sortIndex ?? 0)),
    });
  }

  const stay = stayForDate(doc, date);
  if (stay) {
    const place = doc.places.find((p) => p.id === stay.placeId);
    const hotelName = place?.name ?? stay.chapter;
    const isArrival = stay.startDate === date;
    const progress = stayNightIndex(stay, date);
    const checkInMs = isArrival && stay.checkInFrom
      ? localTimeOnDateMs(date, stay.checkInFrom, stay.timezone)
      : null;
    ranked.push({
      id: `acc-${stay.id}-${date}`,
      kind: "accommodation",
      title: isArrival
        ? `Check-in from ${stay.checkInFrom ?? "the afternoon"} · ${hotelName}`
        : `Staying · ${hotelName}`,
      timeLabel: isArrival
        ? (stay.checkInFrom ? `From ${stay.checkInFrom}` : null)
        : (progress ? `Day ${progress.night} of ${progress.nights}` : null),
      flexible: isArrival,
      confidence: isArrival ? "availability_window_not_appointment" : null,
      note: null,
      href: `/journey/stay/${stay.id}`,
      // Arrival windows are not appointments, but they still sit after inbound
      // travel and before later clocked plans. Dumping them at the end of the
      // day put "from 14:00" below a 16:00 haircut.
      sortMs: isArrival ? (checkInMs ?? 2e15) : 2e15,
    });
  }

  ranked.sort((a, b) => a.sortMs - b.sortMs || (a.event?.sortIndex ?? 0) - (b.event?.sortIndex ?? 0));
  return ranked.map(({ sortMs, ...item }) => {
    void sortMs;
    return item;
  });
}

export function eventTitle(event: TripEvent, doc: TripDocument): string {
  if (event.title) return event.title;
  if (event.kind === "transfer") {
    const booking = doc.bookings.find((b) => b.id === event.bookingId);
    return booking?.title ?? "Transfer";
  }
  if (event.kind === "excursion") {
    const booking = doc.bookings.find((b) => b.id === event.bookingId);
    return booking?.title ?? "Excursion";
  }
  return event.kind;
}

function eventHref(event: TripEvent): string {
  if (event.kind === "transfer") return `/journey/transfer/${event.id}`;
  if (event.kind === "excursion") return `/journey/event/${event.id}`;
  if (event.flightId) return `/journey/flight/${event.flightId}`;
  return `/journey/event/${event.id}`;
}

export type NextAction = {
  kicker: string;
  title: string;
  place?: string;
  primaryLabel: string;
  primaryHref: string;
  note?: string;
  timingConfidence?: string;
  when?: "now" | "today" | "later";
  date?: string;
};

export function selectNextAction(doc: TripDocument, now: Date): NextAction {
  const phase = tripPhase(now, doc.trip.startDate, doc.trip.endDate, doc.trip.homeTimezone);
  if (phase === "before") {
    return upcomingPrep(doc);
  }
  if (phase === "after") {
    const ledger = tripLedger(doc);
    const outstanding = ledger.claims.length > 0
      || Object.values(ledger.supplierByCurrency).some((n) => n > 0)
      || ledger.unallocatedCredits.length > 0;
    return {
      kicker: "After the trip",
      title: outstanding ? "Finish settling what is still open" : "The trip record is here to keep",
      primaryLabel: outstanding ? "Open Money" : "Open the journey",
      primaryHref: outstanding ? "/money" : "/journey",
      note: outstanding
        ? "Travel days are over. Remaining booking balances and reimbursements stay on Money."
        : undefined,
    };
  }

  const nowMs = now.getTime();
  const zone = doc.trip.diaryTimezone;
  const today = todayInZone(now, zone);
  const candidates: Array<{ startMs: number; endMs: number; action: NextAction; rank: number }> = [];

  const airborne = inFlightNow(doc, now);
  if (airborne) {
    return {
      kicker: "Scheduled in flight",
      title: airborne.title,
      primaryLabel: "Open the flight",
      primaryHref: airborne.href,
      note: "Booked times only. A delay does not mean we have reached the next stop.",
    };
  }

  for (const flight of doc.flights) {
    if (flight.progressStatus === "complete") continue;
    const startMs = instantMs(flight.departure.instant);
    const endMs = instantMs(flight.arrival.instant);
    if (endMs <= nowMs) continue;
    const depHm = flight.departure.local.slice(11, 16);
    candidates.push({
      startMs,
      endMs,
      rank: 2,
      action: {
        kicker: `${depHm} ${flight.departure.airport}`,
        title: `${flight.number} ${flight.departure.airport}–${flight.arrival.airport}`,
        primaryLabel: "Open the flight",
        primaryHref: `/journey/flight/${flight.id}`,
        timingConfidence: flight.evidenceStatus,
        note: "Scheduled flight times.",
      },
    });
  }

  for (const event of doc.events) {
    if (event.archived || event.kind === "flight" || event.progressStatus === "complete") continue;
    const startMs = eventClockMs(event, doc);
    if (startMs == null) continue;
    const endMs = eventEndMs(event, startMs);
    if (endMs <= nowMs) continue;
    if (event.pickupMode === "when_ready") {
      const linked = flightById(doc, event.linkedFlightId ?? event.flightId);
      if (linked && nowMs < instantMs(linked.arrival.instant)) continue;
    }
    candidates.push({
      startMs,
      endMs,
      rank: event.pickupMode === "when_ready" ? 1 : 3,
      action: {
        kicker: event.pickupMode === "when_ready" ? "When you're ready at Gate 7" : (event.startLocal ?? event.plannedDeparture ?? "Today"),
        title: eventTitle(event, doc),
        place: event.pickupInstruction ?? undefined,
        primaryLabel: event.kind === "transfer" ? "View pickup instructions" : "Open details",
        primaryHref: eventHref(event),
        note: event.pickupMode === "when_ready"
          ? "Message once bags are collected and you are at the meeting point. Noon is only a planning estimate."
          : undefined,
        timingConfidence: event.timeConfidence ?? event.startConfidence ?? undefined,
      },
    });
  }

  for (const stay of doc.stays) {
    if (!stay.plannedDepartureLocal) continue;
    if (stay.endDate !== today) continue;
    const startMs = localTimeOnDateMs(stay.endDate, stay.plannedDepartureLocal, stay.timezone);
    if (startMs == null || startMs + 90 * 60 * 1000 <= nowMs) continue;
    const place = doc.places.find((p) => p.id === stay.placeId);
    const sameDayFlight = doc.flights.find((f) => f.departure.local.slice(0, 10) === today);
    candidates.push({
      startMs,
      endMs: startMs + 90 * 60 * 1000,
      rank: 2,
      action: {
        kicker: stay.plannedDepartureLocal.includes("T")
          ? stay.plannedDepartureLocal.slice(11, 16)
          : stay.plannedDepartureLocal.slice(0, 5),
        title: sameDayFlight
          ? `Leave ${place?.name ?? stay.chapter} for ${sameDayFlight.departure.airport}`
          : `Leave ${place?.name ?? stay.chapter}`,
        place: place?.address ?? undefined,
        primaryLabel: "Hotel details",
        primaryHref: `/journey/stay/${stay.id}`,
        note: "Our planned departure, not the hotel checkout rule.",
      },
    });
  }

  candidates.sort((a, b) => a.startMs - b.startMs || a.rank - b.rank);
  const active = candidates.filter((row) => row.startMs <= nowMs && nowMs < row.endMs);
  active.sort((a, b) => a.rank - b.rank || a.startMs - b.startMs);
  const todayEnd = localTimeOnDateMs(today, "23:59", zone) ?? nowMs + 24 * 60 * 60 * 1000;
  const todayCandidates = candidates.filter((row) => row.startMs < todayEnd);
  const picked = active[0] ?? todayCandidates[0];
  if (picked) {
    const action = picked.action;
    action.when = active[0] ? "now" : "today";
    action.date = today;
    return action;
  }

  const later = candidates[0];
  const stay = stayForDate(doc, today);
  const place = stay ? doc.places.find((p) => p.id === stay.placeId) : undefined;
  if (!stay && phase === "during") {
    return {
      kicker: "Today",
      title: "Between stays",
      primaryLabel: "Open the journey",
      primaryHref: "/journey",
      note: "A free day to explore. Scheduled times are not a current location.",
      when: "today",
      date: today,
    };
  }
  const laterDate = later ? todayInZone(new Date(later.startMs), zone) : null;
  return {
    kicker: "Free time",
    title: place?.name ? `Staying at ${place.name}` : "A free day to explore",
    place: place?.address ?? undefined,
    primaryLabel: stay ? "Hotel details" : "Browse ideas",
    primaryHref: stay ? `/journey/stay/${stay.id}` : "/journey",
    note: laterDate && laterDate > today
      ? `Next travel day is ${formatDateLabel(laterDate)}. That is not happening now.`
      : "No timed event is next. Free time is intentional.",
    when: "today",
    date: today,
  };
}

function upcomingPrep(doc: TripDocument): NextAction {
  const nextPay = chronologicalSchedule(upcomingPayments(doc)).dated.find((item) => !item.paid);
  if (nextPay) {
    return {
      kicker: "Before we travel",
      title: [
        formatMinor(nextPay.currency, nextPay.amountMinor),
        nextPay.householdLabel ?? "Household share",
        nextPay.date ? formatDateLabel(nextPay.date) : "Date to confirm",
      ].join(" · "),
      place: nextPay.title,
      primaryLabel: "Open Money",
      primaryHref: `/money/${nextPay.bookingId}`,
      note: nextPay.householdLabel
        ? `${nextPay.householdLabel} share still unpaid. Reported unpaid dates stay in history until a payment is recorded.`
        : "Listed upcoming bookings stay unpaid until a payment is recorded. Flight payment status is unknown.",
      when: "later",
    };
  }
  const boat = doc.reviewItems.find((item) => item.id === "boats" && !item.dismissed);
  if (boat) {
    return {
      kicker: "Still to arrange",
      title: "Boat crossing not booked in this pack",
      note: "The van ends at the mainland pier. Tickets and the exact pier remain unknown.",
      primaryLabel: "Open the review list",
      primaryHref: "/essentials/review",
    };
  }
  return {
    kicker: "Before we travel",
    title: `${doc.trip.title} begins ${formatDateLabel(doc.trip.startDate)}`,
    primaryLabel: "Open the journey",
    primaryHref: "/journey",
  };
}

export function countdownDays(now: Date, startDate: string, homeZone: string): number {
  const today = todayInZone(now, homeZone);
  if (today >= startDate) return 0;
  return eachDateInclusive(today, startDate).length - 1;
}

export function countdownLabel(now: Date, startDate: string, homeZone: string): string {
  const days = countdownDays(now, startDate, homeZone);
  if (todayInZone(now, homeZone) >= startDate) return "The journey is under way";
  if (days === 1) return "Tomorrow";
  return `${days} days to departure`;
}

export function flightFacts(doc: TripDocument, flightId: string) {
  const flight = doc.flights.find((f) => f.id === flightId);
  if (!flight) return null;
  return {
    flight,
    duration: formatDurationPretty(flight.departure.instant, flight.arrival.instant),
    overnight: flight.departure.local.slice(0, 10) !== flight.arrival.local.slice(0, 10),
  };
}

export { tripPhase };
